<script lang="ts">
    import {onMount} from 'svelte';
    import {
        Accordion,
        AccordionSection,
        Button,
        Divider,
        FormField,
        CheckboxGroup,
        SnackbarContainer,
        TextField
    } from 'attractions';
    import {
        ChevronDownIcon,
        CopyIcon,
        RefreshCwIcon,
        EyeIcon,
        EyeOffIcon
    } from 'svelte-feather-icons';
    import {
        PasswordGenerator,
        PasswordGeneratorCharGroupTypes, PasswordGeneratorCharGroup, PasswordGeneratorOptions
    } from '../lib/PasswordGenerator';
    import Clipboard from '../lib/Clipboard';
    import GraphemeSplitter from 'grapheme-splitter';
    import Random from "../lib/Random";

    /** Known todo:
     * - make option for excluding similar looking chars
     * - checkboxes are to close, more padding (maybe even row layout or buttons)
     * - clicking on first checkbox reveals "custom chars textbox" (wrong)
     * - create documentation, how it works
     */

    enum InputType {
        Password = "password",
        Text = "text"
    }

    const splitter = new GraphemeSplitter();
    const generator = new PasswordGenerator(new Random());

    const charGroupsConfiguration = [
        {
            value: PasswordGeneratorCharGroupTypes.Digits,
            label: '0-9',
            checked: true,
        }, {
            value: PasswordGeneratorCharGroupTypes.LowerCaseLetters,
            label: 'a-z',
            checked: true,
        }, {
            value: PasswordGeneratorCharGroupTypes.UpperCaseLetters,
            label: 'A-Z',
            checked: true,
        }, {
            value: PasswordGeneratorCharGroupTypes.SpecialChars,
            label: '!$%&...',
            checked: true,
        }, {
            value: PasswordGeneratorCharGroupTypes.CustomChars,
            label: 'Custom',
            checked: false,
        },
    ];


    enum Options {
        UseEveryGroup
    }

    const optionsConfiguration = [
        {
            value: Options.UseEveryGroup,
            label: 'At least one char of every group',
            checked: true,
        }
    ];
/*
    <div role="group">
    <Checkbox value="1" title="At least one char of every group" bind:checked={useEveryGroup}
    on:change="{generatePassword}">
    <span class="ml"> At least one char of every group</span>
    </Checkbox>
    </div>
    */
    let inputPasswordValue = "";
    let inputPasswordType = InputType.Password;

    let passwordLength = 8;
    let customCharsInput = "";
    let useEveryGroup = true;

    let customCharsEnabled = false;

    function generatePassword() {
        const charGroups = buildCharGroups(charGroupsConfiguration);
        const options = new PasswordGeneratorOptions();
        options.useEveryGroup = useEveryGroup;
        inputPasswordValue = generator.generate(charGroups, passwordLength, options);
    }

    function buildCharGroups(charGroupsConfiguration): PasswordGeneratorCharGroup[] {
        let charGroups: PasswordGeneratorCharGroup[] = [];
        charGroupsConfiguration.forEach((allowedCharsConfig) => {
            if (!allowedCharsConfig.checked) {
                return;
            }

            switch (allowedCharsConfig.value) {
                case PasswordGeneratorCharGroupTypes.Digits:
                    charGroups.push(new PasswordGeneratorCharGroup(PasswordGeneratorCharGroupTypes.Digits, PasswordGeneratorCharGroup.DIGITS))
                    break;
                case PasswordGeneratorCharGroupTypes.LowerCaseLetters:
                    charGroups.push(new PasswordGeneratorCharGroup(PasswordGeneratorCharGroupTypes.LowerCaseLetters, PasswordGeneratorCharGroup.LOWER_CASE_LETTERS))
                    break;
                case PasswordGeneratorCharGroupTypes.UpperCaseLetters:
                    charGroups.push(new PasswordGeneratorCharGroup(PasswordGeneratorCharGroupTypes.UpperCaseLetters, PasswordGeneratorCharGroup.UPPER_CASE_LETTERS))
                    break;
                case PasswordGeneratorCharGroupTypes.SpecialChars:
                    charGroups.push(new PasswordGeneratorCharGroup(PasswordGeneratorCharGroupTypes.SpecialChars, PasswordGeneratorCharGroup.SPECIAL_CHARS))
                    break;
                case PasswordGeneratorCharGroupTypes.CustomChars:
                    const customChars = splitter.splitGraphemes(customCharsInput).filter((v, i, a) => a.indexOf(v) === i);
                    charGroups.push(new PasswordGeneratorCharGroup(PasswordGeneratorCharGroupTypes.CustomChars, customChars));
                    break;
            }
        });
        return charGroups;
    }

    function toggleVisibility() {
        inputPasswordType = inputPasswordType === InputType.Password ? InputType.Text : InputType.Password;
    }

    function updatedCustomCharsStatus(e) {
        if (+e.detail.value === PasswordGeneratorCharGroupTypes.CustomChars) {
            customCharsEnabled = e.detail.checked;
        }
    }

    function updatedOptionsConfiguration(e) {
        switch(+e.detail.value) {
            case Options.UseEveryGroup:
                useEveryGroup = e.detail.checked;
                break;
        }
    }



    onMount(() => {
        generatePassword();
    });

</script>
<h2>Password generator</h2>
<SnackbarContainer let:showSnackbar>
    <form>
        <FormField name="Length" help="Password length" class="text-field-shrunk">
            <TextField bind:value={passwordLength} on:input="{generatePassword}" type="number" min="1"/>
        </FormField>

        <FormField name="" help="" class="password-settings">
            <Accordion let:closeOtherPanels>
                <AccordionSection on:panel-open={closeOtherPanels} let:toggle>
                    <div slot="handle">
                        <Button on:click={toggle}>
                            More settings...
                            <ChevronDownIcon size="20" class="ml accordion-chevron"/>
                        </Button>
                    </div>
                    <Divider text="Allowed chars"/>
                    <CheckboxGroup items={charGroupsConfiguration} name="allowedChars[]"
                                   on:change={(e) => {updatedCustomCharsStatus(e);generatePassword();}}/>
                    {#if customCharsEnabled}
                        <TextField outline label="Custom chars" bind:value={customCharsInput} on:input="{generatePassword}"/>
                    {/if}
                    <Divider text="Options"/>
                    <CheckboxGroup items={optionsConfiguration} name="options[]"
                                   on:change={(e) => {updatedOptionsConfiguration(e);generatePassword();}}/>
                </AccordionSection>
            </Accordion>
        </FormField>

        <FormField name="Generated Password" help="">
            <div class="text-field-shrunk">
                <TextField bind:value={inputPasswordValue} on:input="{generatePassword}" on:click="{toggleVisibility}"
                           bind:type="{inputPasswordType}">

                </TextField>
                <Button filled on:click={toggleVisibility}>
                    {#if inputPasswordType === "password"}
                        <EyeIcon size="24" class="item"/>
                    {:else}
                        <EyeOffIcon size="24" class="item"/>
                    {/if}
                </Button>
            </div>
        </FormField>


        <FormField name="" class="pwgen-buttons">
            <Button filled
                    on:click={() =>{ generatePassword(); showSnackbar({ props: { text: `Password generated.`  } })}}>
                <RefreshCwIcon size="20" class="mr"/>&nbsp;Generate
            </Button>
            <Button filled
                    on:click={() =>{ Clipboard.copyTextToClipboard(inputPasswordValue); showSnackbar({ props: { text: `copied ${splitter.countGraphemes(inputPasswordValue)} chars to clipboard.`  } })}}>
                <CopyIcon size="20" class="mr"/>&nbsp;Copy to clipboard
            </Button>
        </FormField>
    </form>


</SnackbarContainer>
